/**
 * CLI handler for generating an API sequence from a workflow.
 *
 * Generates from the latest successful run (default) or from a text prompt.
 * Streams progress events via SSE and outputs the resulting ApiSequenceConfig.
 *
 * Usage:
 *   canary workflow generate-api-sequence <workflowId> [--guidance "text"] [--json]
 *   canary workflow generate-api-sequence --prompt "Create user" [--property <id>] [--json]
 *
 * @module WorkflowApiSequenceCLI
 */

import process from 'node:process';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import { getArgValue, hasFlag } from './auth.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type GenerateResponse = {
  ok: boolean;
  error?: string;
  generationId?: string;
};

type ApiSequenceEvent = {
  emittedAt: string;
  workflowId: string;
} & (
  | { eventType: 'progress'; message: string }
  | { eventType: 'tool-call'; toolName: string; args: unknown }
  | { eventType: 'tool-result'; toolName: string; result: unknown; success: boolean }
  | { eventType: 'text'; delta: string }
  | {
      eventType: 'complete';
      config: unknown;
      summary: string;
      propertyId: string | null;
      credentialId: string | null;
    }
  | { eventType: 'error'; message: string }
);

// ---------------------------------------------------------------------------
// Handler
// ---------------------------------------------------------------------------

export async function handleWorkflowGenerateApiSequence(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const workflowId = argv.find((a) => !a.startsWith('-'));
  const prompt = getArgValue(argv, '--prompt');
  const guidance = getArgValue(argv, '--guidance');
  const propertyId = getArgValue(argv, '--property');
  const jsonOutput = hasFlag(argv, '--json');
  const verbose = hasFlag(argv, '--verbose', '-v');

  if (!workflowId && !prompt) {
    console.error('Error: Provide a workflow ID or --prompt.');
    console.error('');
    console.error('Usage:');
    console.error('  canary workflow generate-api-sequence <workflowId> [--guidance "text"]');
    console.error('  canary workflow generate-api-sequence --prompt "Create user" [--property <id>]');
    process.exit(1);
  }

  // 1. Trigger generation
  let endpoint: string;
  let body: Record<string, unknown>;

  if (workflowId && !prompt) {
    // Generate from latest successful run
    endpoint = '/api-sequence-agent/generate-from-run';
    body = { workflowId };
    if (guidance) body.guidancePrompt = guidance;
    if (!jsonOutput) {
      console.error(`Generating API sequence from latest run of workflow ${workflowId}...`);
    }
  } else {
    // Generate from prompt
    endpoint = '/api-sequence-agent/generate';
    body = { prompt: prompt! };
    if (propertyId) body.propertyId = propertyId;
    if (workflowId) body.workflowId = workflowId;
    if (!jsonOutput) {
      console.error(`Generating API sequence from prompt...`);
    }
  }

  let triggerRes: Response;
  try {
    triggerRes = await fetch(`${apiUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    console.error(`Failed to connect to API: ${err}`);
    process.exit(1);
  }

  if (triggerRes.status === 403) {
    const data = (await triggerRes.json().catch(() => ({}))) as { error?: string };
    console.error(`Error: ${data.error ?? 'This feature is not enabled for your organization.'}`);
    process.exit(1);
  }

  if (!triggerRes.ok) {
    const data = (await triggerRes.json().catch(() => ({}))) as { error?: string };
    console.error(`Error: ${data.error ?? `${triggerRes.status} ${triggerRes.statusText}`}`);
    process.exit(1);
  }

  const triggerData = (await triggerRes.json()) as GenerateResponse;
  if (!triggerData.ok || !triggerData.generationId) {
    console.error(`Error: ${triggerData.error ?? 'Failed to start generation'}`);
    process.exit(1);
  }

  const { generationId } = triggerData;

  // 2. Subscribe to SSE stream
  const streamUrl = `${apiUrl}/api-sequence-agent/stream?generationId=${generationId}`;

  let streamRes: Response;
  try {
    streamRes = await fetch(streamUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
    });
  } catch (err) {
    console.error(`Failed to connect to event stream: ${err}`);
    process.exit(1);
  }

  if (!streamRes.ok || !streamRes.body) {
    console.error(`Failed to connect to event stream: ${streamRes.status}`);
    process.exit(1);
  }

  // 3. Parse SSE events
  let resultConfig: unknown = null;
  let resultSummary = '';
  let hasCompleted = false;
  let exitCode = 0;

  const parser = createParser({
    onEvent: (event: EventSourceMessage) => {
      if (!event.data || event.event !== 'api-sequence-generation') return;

      try {
        const data = JSON.parse(event.data) as ApiSequenceEvent;

        switch (data.eventType) {
          case 'progress':
            if (!jsonOutput) console.error(`  ${data.message}`);
            break;

          case 'tool-call':
            if (verbose) console.error(`  [tool] ${data.toolName}`);
            break;

          case 'tool-result':
            if (verbose) {
              const icon = data.success ? '\u2713' : '\u2717';
              console.error(`  [tool] ${icon} ${data.toolName}`);
            }
            break;

          case 'text':
            // Accumulate but don't print deltas in non-verbose mode
            if (verbose) process.stderr.write(data.delta);
            break;

          case 'complete':
            resultConfig = data.config;
            resultSummary = data.summary;
            hasCompleted = true;
            break;

          case 'error':
            console.error(`\nError: ${data.message}`);
            exitCode = 1;
            hasCompleted = true;
            break;
        }
      } catch {
        // Ignore parse errors for keepalive etc.
      }
    },
  });

  const reader = streamRes.body.getReader();
  const decoder = new TextDecoder();

  try {
    while (!hasCompleted) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
  } finally {
    reader.releaseLock();
  }

  // 4. Output result
  if (exitCode !== 0) {
    process.exit(exitCode);
  }

  if (!resultConfig) {
    console.error('Generation completed but no config was returned.');
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify({ ok: true, config: resultConfig, summary: resultSummary }, null, 2));
  } else {
    if (resultSummary) {
      console.error('');
      console.error(resultSummary);
    }
    console.error('');
    console.log(JSON.stringify(resultConfig, null, 2));
  }
}
